import { ArrowDown, Mail, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PersonalInfo } from "@/types/portfolio";
import { SiteConfig } from "@/types/siteconfig";

interface HeroProps {
  info: PersonalInfo;
  siteConfig?: SiteConfig;
}

export const Hero = ({ info, siteConfig }: HeroProps) => {
  const scrollToSection = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };

  const shouldShow = (field: string) => {
    return info.field_visibility?.[field]?.homepage !== false;
  };

  const showCtaButtons = siteConfig?.hero_show_cta_buttons !== false;
  const ctaTextAbout = siteConfig?.hero_cta_text_about || 'Over mij';
  const ctaTextPortfolio = siteConfig?.hero_cta_text_portfolio || 'Bekijk projecten';
  const ctaTextContact = siteConfig?.hero_cta_text_contact || 'Contact';

  return (
    <section 
      id="home" 
      className="min-h-screen flex items-center justify-center px-4 py-20 hero-gradient relative overflow-hidden"
      aria-label="Hero sectie"
    >
      {/* Subtle background decoration */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden="true">
        <div className="absolute top-1/4 -right-1/4 w-1/2 h-1/2 rounded-full bg-primary/5 blur-3xl" />
        <div className="absolute bottom-1/4 -left-1/4 w-1/2 h-1/2 rounded-full bg-secondary/5 blur-3xl" />
      </div>
      
      <div className="max-w-4xl mx-auto text-center space-y-10 animate-fade-in relative z-10">
        {/* Avatar with refined styling */}
        {shouldShow('avatar') && (
          <div className="flex justify-center">
            <div 
              className="relative w-36 h-36 md:w-44 md:h-44"
              role="img"
              aria-label={`Profielfoto van ${info.first_name} ${info.last_name}`}
            >
              {/* Gradient ring */}
              <div className="absolute inset-0 rounded-full bg-gradient-to-br from-primary via-primary/50 to-secondary p-[3px]">
                <div className="w-full h-full rounded-full bg-background flex items-center justify-center overflow-hidden">
                  {info.avatar ? (
                    <img 
                      src={info.avatar} 
                      alt={`${info.first_name} ${info.last_name}`} 
                      className="w-full h-full rounded-full object-cover"
                      loading="eager"
                    />
                  ) : (
                    <span className="text-4xl md:text-5xl font-bold text-foreground">
                      {`${info.first_name[0]}${info.last_name[0]}`.toUpperCase()}
                    </span>
                  )}
                </div>
              </div>
              {/* Subtle shadow */}
              <div className="absolute inset-0 rounded-full shadow-xl opacity-30" />
            </div>
          </div>
        )}

        {/* Name & Oneliner */}
        <div className="space-y-6">
          <div className="space-y-3">
            <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold tracking-tight">
              <span className="inline-block">
                {shouldShow('first_name') && info.first_name}
              </span>
              {shouldShow('first_name') && shouldShow('last_name') && ' '}
              <span className="inline-block">
                {shouldShow('last_name') && info.last_name}
              </span>
            </h1>
          </div>
          
          <p className="text-lg sm:text-xl md:text-2xl text-muted-foreground max-w-2xl mx-auto leading-relaxed text-balance">
            {info.oneliner}
          </p>
        </div>

        {/* CTAs with improved styling */}
        {showCtaButtons && (
          <nav 
            className="flex flex-col sm:flex-row gap-4 justify-center items-center pt-2"
            aria-label="Navigatie knoppen"
          >
            {siteConfig?.show_contact !== false && (
              <Button
                size="lg"
                onClick={() => scrollToSection("contact")}
                className="group w-full sm:w-auto min-w-[160px] h-12 text-base font-medium shadow-lg shadow-primary/20 hover:shadow-xl hover:shadow-primary/25 transition-all"
                aria-label="Ga naar contact sectie"
              >
                <Mail className="mr-2 h-5 w-5" aria-hidden="true" />
                {ctaTextContact}
              </Button>
            )}
            {siteConfig?.show_about !== false && (
              <Button
                size="lg"
                variant="secondary"
                onClick={() => scrollToSection("about")}
                className="group w-full sm:w-auto min-w-[160px] h-12 text-base font-medium"
                aria-label="Ga naar over mij sectie"
              >
                {ctaTextAbout}
              </Button>
            )}
            {siteConfig?.show_portfolio !== false && (
              <Button
                size="lg"
                variant="outline"
                onClick={() => scrollToSection("portfolio")}
                className="group w-full sm:w-auto min-w-[160px] h-12 text-base font-medium border-2 hover:bg-muted/50"
                aria-label="Ga naar portfolio sectie"
              >
                {ctaTextPortfolio}
                <ArrowDown className="ml-2 h-5 w-5 group-hover:translate-y-1 transition-transform duration-200" aria-hidden="true" />
              </Button>
            )}
          </nav>
        )}
      </div>
    </section>
  );
};